import { useState, useEffect } from 'react';
import * as api from '../../api/adminApi';
import { PageHeader, StatCard, StatRow, FilterPills, Pagination, cardStyle, tableHeaderStyle, tableCellStyle, LoadingState, ErrorState, timeAgo } from './shared';

export default function DocumentsPage() {
  const [summary, setSummary] = useState<Awaited<ReturnType<typeof api.getDocumentsSummary>> | null>(null);
  const [docs, setDocs] = useState<Awaited<ReturnType<typeof api.getDocuments>> | null>(null);
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = async () => {
    try {
      setLoading(true);
      const [s, d] = await Promise.all([
        api.getDocumentsSummary(),
        api.getDocuments({ search: search || undefined, type: typeFilter === 'all' ? undefined : typeFilter, page }),
      ]);
      setSummary(s); setDocs(d);
    } catch (err: unknown) { setError((err as Error).message); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [page, typeFilter, search]);

  if (loading && !summary) return <LoadingState />;
  if (error) return <ErrorState message={error} onRetry={load} />;

  return (
    <div>
      <PageHeader title="Documents" subtitle="All conversations & documents across users">
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <div style={{ position: 'relative' }}>
            <input type="text" placeholder="Search documents..." value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(1); }}
              style={{ padding: '8px 12px 8px 36px', fontSize: 13, borderRadius: 8, border: '1px solid #E8E0D8', backgroundColor: '#fff', width: 200, fontFamily: '"Geist", system-ui, sans-serif', outline: 'none' }}
            />
            <svg style={{ position: 'absolute', left: 10, top: 9, color: '#8B7D6B' }} width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
          </div>
          <button style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 16px', borderRadius: 8, border: '1px solid #E8E0D8', backgroundColor: '#fff', fontSize: 13, fontWeight: 500, cursor: 'pointer', fontFamily: '"Geist", system-ui, sans-serif' }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" /></svg>
            Filters
          </button>
        </div>
      </PageHeader>

      <StatRow>
        <StatCard label="TOTAL CONVERSATIONS" value={summary?.totalConversations.toLocaleString() ?? '0'} />
        <StatCard label="IMPORTED DOCS" value={summary?.importedDocs.toLocaleString() ?? '0'} />
        <StatCard label="SHARED DOCS" value={summary?.sharedDocs.toLocaleString() ?? '0'} />
        <StatCard label="STORAGE USED" value={`${summary?.storageUsedMB ? (summary.storageUsedMB / 1024).toFixed(1) : '0'} GB`} />
      </StatRow>

      <div style={cardStyle}>
        <FilterPills
          options={[
            { label: 'All', value: 'all' },
            { label: 'Conversations', value: 'conversations' },
            { label: 'Imported', value: 'imported' },
            { label: 'Shared', value: 'shared' },
            { label: 'Trashed', value: 'trashed' },
          ]}
          value={typeFilter}
          onChange={(v) => { setTypeFilter(v); setPage(1); }}
        />

        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <th style={tableHeaderStyle}>Title</th>
              <th style={tableHeaderStyle}>Owner</th>
              <th style={tableHeaderStyle}>Type</th>
              <th style={tableHeaderStyle}>Msgs</th>
              <th style={tableHeaderStyle}>Tools</th>
              <th style={tableHeaderStyle}>Last Active</th>
            </tr>
          </thead>
          <tbody>
            {docs?.documents.map((d) => {
              const isTrashed = !!d.deletedAt;
              return (
                <tr key={d._id} style={isTrashed ? { opacity: 0.5 } : {}}>
                  <td style={tableCellStyle}>
                    <div style={{ fontWeight: 500, color: isTrashed ? '#8B7D6B' : '#1A1714' }}>{d.title}</div>
                    {d.subtitle && <div style={{ fontSize: 12, color: '#8B7D6B', marginTop: 2 }}>{d.subtitle}</div>}
                  </td>
                  <td style={tableCellStyle}>{typeof d.userId === 'object' ? d.userId.displayName : '—'}</td>
                  <td style={tableCellStyle}>
                    {isTrashed ? <span style={{ color: '#8B7D6B', fontSize: 13 }}>Trashed</span> : <TypeBadge tools={d.toolsUsed} />}
                  </td>
                  <td style={tableCellStyle}>{d.messageCount}</td>
                  <td style={{ ...tableCellStyle, fontSize: 13, color: '#8B7D6B' }}>{d.toolsUsed?.join(', ') || '—'}</td>
                  <td style={{ ...tableCellStyle, color: '#8B7D6B' }}>{timeAgo(d.lastMessageAt)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>

        {docs && <Pagination page={docs.page} totalPages={docs.totalPages} total={docs.total} label="documents" onPageChange={setPage} />}
      </div>
    </div>
  );
}

function TypeBadge({ tools }: { tools: string[] }) {
  if (!tools || tools.length === 0) return <span style={{ color: '#8B7D6B', fontSize: 13 }}>—</span>;
  const colors: Record<string, { bg: string; text: string }> = {
    draft: { bg: '#E8F0FE', text: '#1565C0' },
    review: { bg: '#FFF3E0', text: '#D46A3A' },
    esign: { bg: '#F3E5F5', text: '#7C3AED' },
    research: { bg: '#E8F5E9', text: '#22883F' },
    workflow: { bg: '#E3F2FD', text: '#1565C0' },
    shared: { bg: '#E8F5E9', text: '#22883F' },
  };
  const key = tools[0]?.toLowerCase() ?? '';
  const c = colors[key] ?? { bg: '#F0EBE4', text: '#8B7D6B' };
  const label = tools.length > 1 ? `${tools[0]} + ${tools.length - 1}` : tools[0];
  return <span style={{ backgroundColor: c.bg, color: c.text, padding: '3px 10px', borderRadius: 4, fontSize: 12, fontWeight: 600 }}>{label}</span>;
}
